//Single function to handle FM & GA4 tracking
import get from 'lodash.get';
import { AppConfig } from '../AppConfig';
import { getUTMParameters, setUTMParamInCookie } from '../cookieUtils';
import {
  excludedRegexList,
  getCategoryName,
  includedRegexList,
} from '../getCategory';
import tracker from '../tracker-event';
import { TrackingProperties } from '../tracking-events';
import {
  deleteSensitiveUserInfo,
  encryptSensitiveUserInfo,
  isObject,
  objectToString,
  stringToObject,
  toLowerKeys,
} from './utils';

/**
 * @param {string} label - position of the element and type of the element
 * @param {string} value - optional value
 * @param {EventTarget} obj - triggered element to get the button text or div text using data-label attribute
 * @param {string} action - user action (Click, Click_Error, Click_Submit, Click_Success, Click_Play)
 * @param {string} url - if url is not in the obj passed
 * @param {Record<string, string>} valueObj - Any additional values to be tracked
 *
 * @example onClick={(e)=> handleTrack({label: 'navbar_button', obj: e.target, valueObj: {FormType: 'tax-experts', EmailID: 'test@gmail.com'}}) data-label='Explore GST'}
 * (data-label is optional, add if e.target element has multiple text nodes or long text)
 */

export type THandleTrack = {
  label: string;
  value?: string;
  obj?: EventTarget | null | HTMLElement;
  action?: string;
  url?: string;
  valueObj?: Record<string, string | unknown>;
  categoryName?: string;
  parentCategory?: string;
};

type TLogObj = {
  track_Label: string;
  track_Value: string;
  targetPage: string;
  isSSO: boolean;
  parentCategory: string;
  categoryName: string;
};

const logObj = {} as TLogObj;

/**
 * Safely access localStorage - handles SecurityError in Safari private mode
 * and other cases where localStorage is unavailable
 */
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return window.localStorage.getItem(key);
      }
    } catch (error) {
      // Safari private mode throws SecurityError (DOMException code 18)
      // Safe to ignore - tracking will continue without localStorage
      console.warn('localStorage access blocked:', error);
    }
    return null;
  },
  setItem: (key: string, value: string): void => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(key, value);
      }
    } catch (error) {
      // Safari private mode throws SecurityError (DOMException code 18)
      // Safe to ignore - tracking will continue without persisting data
      console.warn('localStorage access blocked:', error);
    }
  },
};

export const handleTrack = ({
  label = '',
  value = '',
  obj = null,
  action = 'Click',
  url = '',
  valueObj = {},
  categoryName = '',
  parentCategory = getCategoryName({
    regexList: includedRegexList.concat(excludedRegexList),
    parent: true,
  }) || 'Uncategeroized',
}: THandleTrack): void => {
  logObj.track_Label = label;
  logObj.track_Value = '{}redirect_url :: ' + url;

  const clickedElement = obj as HTMLElement;
  const anchorElement = clickedElement?.closest('a');

  if (clickedElement) {
    const textContext = get(clickedElement, 'textContent', '')?.trim();
    const dataLabel = clickedElement.dataset.label || textContext;

    logObj.track_Label = `${label}_${dataLabel}`;
    logObj.track_Value += `${(anchorElement?.href || '') + value}`;
  }

  if (isObject(valueObj)) {
    const nestedObj = {} as Record<string, string>;
    Object.entries(valueObj).forEach(([key, valueData]) => {
      //if valueData is an object convert the key value pairs to a string, as nested objects are not supported in GA
      const nestedPairs =
        isObject(valueData) &&
        objectToString(valueData as Record<string, string>, '{|}', ' ::: ');
      nestedObj[key] = nestedPairs || (valueData as string);
    });

    //Addition of delimeters on each key value pairs
    logObj.track_Value += nestedObj && objectToString(nestedObj, '{}', ' :: ');
  }

  //Carry tracking label of the clicked button to redirected page
  const urlRegex = /\bhttps?:\/\/\S+/gi;
  const targetPage = anchorElement?.href || logObj.track_Value.match(urlRegex);
  if (targetPage) {
    logObj.targetPage = targetPage.toString();
    if (logObj.targetPage.includes('accounts.cleartax.in')) {
      logObj.isSSO = true;
    }
    safeLocalStorage.setItem('logObj', JSON.stringify(logObj));
  }

  logObj.categoryName = window.categoryName
    ? get(window, ['categoryName'], 'Uncategorized')
    : categoryName;
  logObj.parentCategory = window.parentCategory
    ? get(window, ['parentCategory'], logObj.categoryName)
    : parentCategory;

  //To save encrypted values along with non-encrypted ones in funnel for mapping it with GA
  const trackValueObj = stringToObject(logObj.track_Value, '{}', ' :: ');
  const encryptData = encryptSensitiveUserInfo(trackValueObj);
  const funnelCategory = logObj.parentCategory + '_' + logObj.categoryName;

  ct_internalTracking(
    funnelCategory,
    action,
    logObj.track_Label,
    objectToString(encryptData, '{}', ' :: ')
  );

  const hasUserEmail = !!safeLocalStorage.getItem('userEmail');

  if (hasUserEmail) {
    const trackEventsProps: Partial<TrackingProperties> = {
      category: logObj.parentCategory,
      page: window.location.href,
    };
    if (logObj.track_Label) {
      trackEventsProps.cta_name = logObj.track_Label;
    }
    tracker.trackEvent(action === 'Click' ? 'click' : action, trackEventsProps);
  }
  //GA4 tracking
  handleTrackGA4(action, logObj, encryptData);
};

const handleTrackGA4 = (
  action: string,
  logObjValue: TLogObj,
  trackValueObj: Record<string, string>
): void => {
  const { targetPage, track_Label } = logObjValue;
  const cloneObj = { ...trackValueObj };
  const data = deleteSensitiveUserInfo(cloneObj);

  const path = window.location.pathname;

  setUTMParamInCookie(window.location.search);

  const newGAObj = {
    parent_category: logObjValue.parentCategory,
    sub_category: logObjValue.categoryName,
    content_group: logObjValue.parentCategory + '_' + logObjValue.categoryName,
    event_label: track_Label,
    current_page_pathname: path.endsWith('/')
      ? path.slice(0, path.length - 1)
      : path,
    redirect_url: targetPage,
    ...data,
    send_to: AppConfig.GA4_TAG.allEvents,
    ...getUTMParameters(),
  };

  if (window.gtag) {
    window.gtag('event', action.toLowerCase(), toLowerKeys(newGAObj));
  }
};

const TRACKING_ENDPOINT = '/funnelmetrics';

const serialize = (obj: Record<string, unknown>): string => {
  return Object.entries(obj)
    .filter(([, value]) => value != null)
    .map(([key, value]) => {
      const data = (value || '') as string;
      return `${encodeURIComponent(key)}=${encodeURIComponent(data)}`;
    })
    .join('&');
};

const ct_internalTracking = (
  category: string,
  action: string,
  label: string,
  value: string
) => {
  const qs = serialize({
    category,
    action,
    label,
    value,
    ...getUTMParameters(),
    original_referrer: document.referrer,
    _t: new Date().getTime(),
  });
  const img = new Image();
  img.src = TRACKING_ENDPOINT + '?' + qs;
};
